/**
 * Routing — push each finding to the person who can act on it.
 *
 * The reviewer's rule, made mechanical:
 *
 * | Cause | Goes to | Via | Asks for |
 * |---|---|---|---|
 * | `operator` | Operating Supervisor, Superintendent, Mine Control | coaching, pre-start brief, shift handover, FMS restriction on a critical alert | brief / coaching the named driver; watch in the FMS; restrict if critical |
 * | `equipment` | Reliability Engineer, Maintenance Planner | RCA / FMEA review, work order, parts reservation | RCA and FMEA update; schedule the part — with part number, site stock and lead time |
 * | `mixed` | both of the above | both | both |
 * | `insufficient` | Reliability Engineer | triage queue | a look, not an action |
 *
 * Names come from `seed/site-roles.ts`; each route carries the holder's
 * `synthesised` flag so a page can mark stand-ins. Part numbers come from the
 * demo's `PART_OF_COMP` map; description, lead time and site-store stock from
 * `parts.json` when the caller supplies it.
 *
 * {@link routingDecisions} produces one decision per open alert (seven) plus
 * one per flagged machine × component — every `operator` or `mixed` finding,
 * and every `equipment` finding on a component in poor condition or worse.
 *
 * Engine layer (layer 2 of the 4-layer architecture): pure functions over
 * fetched data. No React, no clock, no randomness.
 */

import { BY_ID } from '../../data/core.ts';
import type {
  RootCauseFinding,
  RouteChannel,
  RouteTarget,
  RoutingAction,
  RoutingDecision,
  SiteRole,
} from '../../schema/operations.ts';
import type { CataloguePart, InventoryLine } from '../../schema/part.ts';
import { PART_OF_COMP } from '../oem-engine.ts';
import { ALERTS, type Alert } from '../seed/alerts.ts';
import { FMEA_SEED } from '../seed/fmea.ts';
import { siteRole } from '../seed/site-roles.ts';
import { TARPS } from '../seed/tarps.ts';
import { attribute } from './attribution.ts';
import { loadReliabilityInputs } from './inputs.ts';
import { rootCause, rootCauses, type RootCauseCtx } from './root-cause.ts';

/** What routing reads: the root-cause context plus, optionally, the parts domain. */
export interface RoutingCtx extends RootCauseCtx {
  readonly parts?: readonly CataloguePart[];
  readonly inventory?: readonly InventoryLine[];
}

/** A part the planner should schedule. */
export interface PartPick {
  partNo: string;
  description: string | null;
  leadTimeDays: number | null;
  siteStock: number | null;
}

/** The first part `PART_OF_COMP` maps a component to, priced from the parts domain when present. */
export function partFor(machine: string, component: string, ctx: Pick<RoutingCtx, 'parts' | 'inventory'>): PartPick | null {
  const hit = PART_OF_COMP.find(([re]) => re.test(component));
  if (!hit) return null;
  const partNo = hit[1][0];
  const cat = ctx.parts?.find((p) => p.part_no === partNo);
  const loc = `${BY_ID[machine].mine} site store`;
  const stock = ctx.inventory?.find((i) => i.part_no === partNo && i.location === loc);
  return {
    partNo,
    description: cat?.description ?? null,
    leadTimeDays: cat?.lead_time_days ?? null,
    siteStock: ctx.inventory ? (stock?.qty_on_hand ?? 0) : null,
  };
}

/** The FMEA row covering a machine's class × component, if any. */
export function fmeaFor(machine: string, component: string): string | null {
  const cls = BY_ID[machine].cls;
  return FMEA_SEED.find((f) => f.class === cls && new RegExp(f.match, 'i').test(component))?.id ?? null;
}

/** The TARP covering a machine's class × component, if any. */
export function tarpFor(machine: string, component: string): string | null {
  const m = BY_ID[machine];
  return (
    TARPS.find(
      (t) =>
        (t.class === m.cls || t.alsoClasses?.includes(m.cls)) &&
        (t.appliesTo !== 'battery' || m.battery) &&
        (t.component === component || new RegExp(t.component, 'i').test(component)),
    )?.id ?? null
  );
}

function target(finding: RootCauseFinding, role: SiteRole, channel: RouteChannel, why: string): RouteTarget {
  const holder = siteRole(finding.mine, role);
  return {
    role,
    personName: holder.personName,
    ...(holder.personId ? { personId: holder.personId } : {}),
    mine: finding.mine,
    channel,
    why,
    synthesised: holder.synthesised,
  };
}

/** Route one finding. Pass the alert when the finding answers one. */
export function routeFinding(
  finding: RootCauseFinding,
  ctx: Pick<RoutingCtx, 'parts' | 'inventory'> = {},
  alert?: Alert,
): RoutingDecision {
  const routes: RouteTarget[] = [];
  const actions: RoutingAction[] = [];
  const who = finding.topOperator;
  const critical = alert?.sev === 'crit';
  const tarpId = tarpFor(finding.machine, finding.component);
  const fmeaId = fmeaFor(finding.machine, finding.component);

  if (finding.cause === 'operator' || finding.cause === 'mixed') {
    const driver = who ? `${who.name} (crew ${who.crew}, ${who.shift === 'N' ? 'night' : 'day'} shift)` : 'the crew';
    routes.push(
      target(
        finding,
        'Operating Supervisor',
        'Coaching session',
        `${who ? `${who.name} carries ${Math.round(who.eventShare * 100)}% of the events on ${Math.round(who.hoursShare * 100)}% of the hours` : 'Events concentrate on one driver'} — a technique issue for the crew supervisor.`,
      ),
      target(
        finding,
        'Superintendent',
        critical ? 'FMS restriction' : 'Shift handover',
        critical
          ? 'The alert is critical — a production restriction needs the superintendent.'
          : 'Visibility of a repeat operator pattern on the superintendent\'s crew.',
      ),
      target(
        finding,
        'Mine Control',
        'Shift handover',
        `Mine Control watches ${finding.machine} on the FMS and carries it on the ${who?.shift === 'N' ? 'night' : 'day'}-shift handover.`,
      ),
    );
    actions.push(
      {
        kind: 'coaching',
        owner: 'Operating Supervisor',
        text: `Coach ${driver} on ${finding.component.toLowerCase()} technique; trainer ride-along next shift.`,
      },
      {
        kind: 'brief',
        owner: 'Operating Supervisor',
        text: `Pre-start brief to ${who ? `crew ${who.crew}` : 'the crew'} on ${finding.machine}'s ${finding.component.toLowerCase()} pattern.`,
      },
      {
        kind: 'handover',
        owner: 'Mine Control',
        text: `${finding.machine} on the shift-handover list${tarpId ? ` at its current ${tarpId} level` : ''}.`,
      },
    );
    if (critical) {
      actions.push({
        kind: 'restriction',
        owner: 'Superintendent',
        text: alert?.window ? `Restriction: ${alert.window}.` : `Restrict ${finding.machine} until inspected.`,
      });
    }
  }

  if (finding.cause === 'equipment' || finding.cause === 'mixed') {
    const part = partFor(finding.machine, finding.component, ctx);
    routes.push(
      target(
        finding,
        'Reliability Engineer',
        'RCA / FMEA review',
        `${finding.component} is the condition signal — root cause and FMEA${fmeaId ? ` (${fmeaId})` : ''} belong with reliability.`,
      ),
      target(
        finding,
        'Maintenance Planner',
        part ? 'Parts reservation' : 'Work order',
        part
          ? `Schedule ${part.partNo}${part.description ? ` (${part.description})` : ''} into the next window.`
          : `Schedule the ${finding.component.toLowerCase()} inspection into the next window.`,
      ),
    );
    actions.push({
      kind: 'rca',
      owner: 'Reliability Engineer',
      text: `RCA on ${finding.machine} ${finding.component.toLowerCase()}${finding.cause === 'mixed' ? ' — separate wear from technique before the next change-out' : ''}.`,
    });
    if (fmeaId) {
      actions.push({
        kind: 'fmea-update',
        owner: 'Reliability Engineer',
        text: `Update ${fmeaId} occurrence and detection with this event.`,
      });
    }
    if (part) {
      const stockText =
        part.siteStock === null
          ? ''
          : part.siteStock > 0
            ? ` — ${part.siteStock} on the ${finding.mine} site shelf`
            : ` — none at ${finding.mine}, ${part.leadTimeDays ?? '?'} day lead time`;
      actions.push({
        kind: 'schedule-parts',
        owner: 'Maintenance Planner',
        text: `Reserve ${part.partNo}${part.description ? ` (${part.description})` : ''}${stockText}.`,
        partNo: part.partNo,
        ...(part.siteStock !== null ? { siteStock: part.siteStock } : {}),
        ...(part.leadTimeDays !== null ? { leadTimeDays: part.leadTimeDays } : {}),
      });
    }
    actions.push({
      kind: 'work-order',
      owner: 'Maintenance Planner',
      text: alert?.window
        ? `Work order in the window: ${alert.window}.`
        : `Inspection work order at the next planned stop.`,
    });
  }

  if (finding.cause === 'insufficient') {
    routes.push(
      target(
        finding,
        'Reliability Engineer',
        'Triage queue',
        'Too little signal to call operator or equipment — a reliability look before anyone acts.',
      ),
    );
    actions.push({
      kind: 'triage',
      owner: 'Reliability Engineer',
      text: `Review ${finding.machine} ${finding.component.toLowerCase()} at the weekly reliability meeting.`,
    });
  }

  return {
    ...(alert ? { alertId: alert.id } : {}),
    machine: finding.machine,
    mine: finding.mine,
    component: finding.component,
    cause: finding.cause,
    confidence: finding.confidence,
    evidence: finding.evidence,
    routes,
    actions,
    source: alert ? 'alert' : 'flagged',
  };
}

/** Route one open alert: classify its machine × component, then route. */
export function routeAlert(alert: Alert, ctx: RoutingCtx): RoutingDecision {
  return routeFinding(rootCause(alert.machine, alert.comp, ctx), ctx, alert);
}

/**
 * A decision for every open alert, then every flagged machine × component not
 * already covered by an alert: all `operator` and `mixed` findings, and
 * `equipment` findings on a component in poor condition or worse (signal
 * ≥ 0.65). Alerts first in `ALERTS` order; flagged by confidence.
 */
export function routingDecisions(ctx: RoutingCtx): RoutingDecision[] {
  const fromAlerts = ALERTS.map((a) => routeAlert(a, ctx));
  const covered = new Set(ALERTS.map((a) => `${a.machine}|${a.comp}`));
  const flagged = rootCauses(ctx)
    .filter((f) => !covered.has(`${f.machine}|${f.component}`))
    .filter((f) => f.cause === 'operator' || f.cause === 'mixed' || (f.cause === 'equipment' && f.equipmentSignal >= 0.65))
    .map((f) => routeFinding(f, ctx));
  return [...fromAlerts, ...flagged];
}

/** Fetch everything and route. */
export async function loadRoutingDecisions(): Promise<RoutingDecision[]> {
  const inputs = await loadReliabilityInputs();
  return routingDecisions({ ...inputs, attribution: attribute(inputs) });
}
